import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { getLoginPath } from '@/lib/loginPath';
import { useSession } from '@/hooks/useSession';
import { MobileAdminNav } from '@/components/MobileAdminNav';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { HeaderAccountBar } from '@/components/HeaderAccountBar';
import { SiteScopeBar } from '@/components/SiteScopeBar';
import { MediaStorageProvider } from '@/components/MediaStorageProvider';
import { isSiteScopedPath } from '@/lib/siteScopedRoutes';
import { APP_BRAND_TITLE } from '@/lib/appVersion';

export function MobileAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { ok, refresh, currentSiteId } = useSession();
  const [pwdOpen, setPwdOpen] = useState(false);
  const [wideScreen, setWideScreen] = useState(false);
  const siteScoped = isSiteScopedPath(location.pathname);

  useEffect(() => {
    const onRefresh = () => void refresh();
    window.addEventListener('wes-session-refresh', onRefresh);
    return () => window.removeEventListener('wes-session-refresh', onRefresh);
  }, [refresh]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    function onMq() {
      setWideScreen(mq.matches);
    }
    onMq();
    mq.addEventListener('change', onMq);
    return () => mq.removeEventListener('change', onMq);
  }, []);

  async function logout() {
    await apiFetch('logout', { method: 'POST', body: '{}' });
    navigate(getLoginPath(), { replace: true });
  }

  return (
    <MediaStorageProvider>
      <div className="flex min-h-screen flex-col bg-app-mesh pb-[calc(6.25rem+env(safe-area-inset-bottom))]">
        <header
          className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-xl"
          style={{ paddingTop: 'max(0.35rem, env(safe-area-inset-top))' }}
        >
          {wideScreen ? (
            <div className="border-b border-teal-200/60 bg-teal-50/90 px-3 py-2 text-center text-xs text-teal-900">
              当前为大屏视图，可使用
              <Link to="/dashboard" className="mx-1 font-semibold underline underline-offset-2">
                完整电脑后台
              </Link>
              （侧栏与全部功能）。
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-icon text-sm text-white shadow-md ring-1 ring-white/10">
              <i className="fa-solid fa-shrimp" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-bold tracking-tight text-slate-900">{APP_BRAND_TITLE} · 日常管理</p>
              <p className="text-[11px] text-slate-500">手机端快捷入口</p>
            </div>
            {ok ? (
              <HeaderAccountBar onChangePassword={() => setPwdOpen(true)} className="w-full sm:w-auto" />
            ) : null}
            <button
              type="button"
              className="ml-auto shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200/90 active:bg-slate-100"
              onClick={() => void logout()}
            >
              退出
            </button>
          </div>
        </header>

        <main className="min-h-0 flex-1 px-3 pt-4">
          <div className="mx-auto max-w-lg pb-4">
            {ok && siteScoped ? <SiteScopeBar compact className="mb-4" /> : null}
            <Outlet key={siteScoped && currentSiteId > 0 ? currentSiteId : 'session'} />
          </div>
        </main>

        <MobileAdminNav />

        <ChangePasswordModal open={pwdOpen} onClose={() => setPwdOpen(false)} onSuccess={() => void refresh()} />
      </div>
    </MediaStorageProvider>
  );
}
